import React from 'react';
import { ArrowRight, ShieldCheck, Timer, CalendarCheck, Truck } from 'lucide-react';
import { whyChooseTiles } from '../../mock';
import { useBooking } from '../../BookingContext';

const iconMap = { ShieldCheck, Timer, CalendarCheck, Truck };

export default function WhyChoose() {
  const { open: onBook } = useBooking();
  return (
    <section id="why" className="py-16 md:py-24 bg-gradient-to-b from-pink-50/30 to-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="zp-section-title">
            Why Choose <span className="zp-gradient-text">Zoopers</span>
          </h2>
          <p className="mt-4 text-slate-600">
            Zoopers is dedicated to providing exceptional tailoring and alteration services that cater to your individual needs. Our commitment to quality, convenience, and customer satisfaction sets us apart.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {/* Card 1 — Women Experts */}
          <div className="relative rounded-3xl bg-pink-50 p-8 border border-pink-100 overflow-hidden">
            <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-pink-200/60" />
            <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-pink-200/50" />
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Driven by Women Experts in Measurement</h3>
                <p className="mt-2 text-sm text-slate-600">A friendly, all-women team of measurement experts ensures your comfort and privacy at every visit.</p>
                <button onClick={onBook} className="mt-5 inline-flex items-center gap-2 zp-pill-btn text-sm">
                  Download App <ArrowRight size={16} />
                </button>
              </div>
              <img
                src="https://images.unsplash.com/photo-1753162658653-d33c53910d9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Women measurement experts"
                className="rounded-2xl w-full h-52 object-cover shadow-lg"
              />
            </div>
          </div>

          {/* Card 2 — App Download */}
          <div className="relative rounded-3xl bg-pink-50 p-8 border border-pink-100 flex flex-col justify-between overflow-hidden">
            <div className="absolute -top-8 -left-8 h-40 w-40 rounded-full bg-pink-200/60" />
            <div className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full bg-pink-200/50" />
            <div className="relative">
              <h3 className="text-xl font-bold text-slate-900">Download our app for seamless booking, tracking and doorstep delivery</h3>
              <p className="mt-2 text-sm text-slate-600">Available on App Store and Google Play.</p>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.zoopers.app&hl=en_IN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-[#12121A] hover:bg-slate-800 text-white rounded-2xl px-5 py-3 transition-colors text-sm font-semibold"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76a2.07 2.07 0 0 0 .47.24l10.3-10.3-2.97-2.97L3.18 23.76zM20.52 10.4l-2.8-1.6-3.35 3.22 3.35 3.35 2.8-1.62a2.09 2.09 0 0 0 0-3.35zM3 .24a2.09 2.09 0 0 0-.47.24L10.98 9l2.97-2.97L3 .24zM14.25 12 3.65 22.6a2 2 0 0 0 1.2-.2l11.73-6.78-2.33-2.62z"/></svg>
                  Get it on Google Play
                </a>
                <a
                  href="https://apps.apple.com/us/app/zoopers-now/id6762121370"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 bg-[#12121A] hover:bg-slate-800 text-white rounded-2xl px-5 py-3 transition-colors text-sm font-semibold"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  Download on App Store
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 4 sticker tiles */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {whyChooseTiles.map((tile) => {
            const Icon = iconMap[tile.icon] || ShieldCheck;
            return (
              <div key={tile.title} className={`${tile.bg} rounded-3xl p-5 flex flex-col items-center text-center`}>
                <div className={`${tile.accent} text-4xl mb-2`}>
                  <Icon size={36} />
                </div>
                <p className="text-slate-900 text-base font-extrabold leading-tight mb-1">{tile.title}</p>
                <p className={`${tile.accent} text-xs font-medium leading-normal mb-3`}>{tile.subtitle}</p>
                <a
                  href={tile.href}
                  className={`inline-flex items-center justify-center rounded-full h-8 px-4 ${tile.btn} text-white text-xs font-semibold transition-colors`}
                >
                  {tile.cta}
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
